import * as moment from 'moment';
import lodash from 'lodash';

import {
    PDFWriter
} from 'complionUtil/PDFWriter';
import { DEFAULT_STUDY_DETAILS_HEADERS, DATE_FORMAT } from './DelegationAuditLog';
import { formatDelegationSignatureTableRow, formatDelegationTableRow, formatSignatureManifestationHeader } from './delegationSummaryTemplates';


const DEFAULT_PI_TESTAMENT = `
I have delegated to each individual above the authority to perform the tasks indicated, under my supervision. 
As of the start date, each individual was qualified to perform the delegated task(s) on the basis of education,
training or experience. (See FDA guidance for Industry Investigator Responsibilities - Protecting the Rights, 
Safety, and Welfare of Study Subjects.)
Electronic Signature(s) Attached`;
const DEFAULT_STAFF_TESTAMENT = `
By signing, I confirm/acknowledge that I accept responsibility for and am appropriately trained, skilled and qualified for the tasks listed herein
Mechanisms are in place to ensure that I receive the appropriate information and training throughout the study and that a 2-way communication channel
exists between P.I. and self.
Electronic Signature(s) Attached`;


const DEFAULT_TESTAMENT_MAP = {
    PI: DEFAULT_PI_TESTAMENT,
    Staff: DEFAULT_STAFF_TESTAMENT
};

const DEFAULT_DELEGATION_SUMMARY_COLUMN_HEADERS = [['Name Role', 'Delegations', 'Start Date', 'PI Signature', 'Staff Signature', 'End Date', 'PI Signature']];
const DEFAULT_SIGNATURE_MANIFESTATION_COLUMN_HEADERS = [['Name', 'Email Address', 'Date Signed', 'Testament Signed']];


export default class DelegationSummary {
    /**
     * @param {import('./DelegationAuditLog').DOA} DOA 
     * @param {String} timezoneOffset The timezone offset associated with the study.
     * @param {PDFWriter} PDFWriter A PDF utility class used to write audit log data to a PDF.
     */
    constructor(DOA, timezoneOffset, PDFWriterClass = PDFWriter) {
        this.DOA = DOA;
        this.timezoneOffset = timezoneOffset;
        this.pdfWriter = new PDFWriterClass();
    }

    /**
     * Generates the PDF representation of a DOA summary.
     */
    generate() {
        this.generateHeader();
        this.generateStudyKey();
        this.generateTestamentSection();
        this.generateDelegationSummary();
        this.generateSignatureManifestations();
        this.save();
    }

    /**
     * Appends sections of the DOA summary to an existing pdf
     * @param {PDFWriter} pdfWriter 
     */
    append(pdfWriter) {
        this.pdfWriter = pdfWriter;
        this.generateHeader();
        this.generateStudyKey();
        this.generateTestamentSection();
        this.generateDelegationSummary();
        this.generateSignatureManifestations();
    }

    /**
     * Generates The Main Delegation Summary Header with study details.
     * @private
     */
    generateHeader() {
        const studyBinder = this.DOA.studyBinder;
        this.pdfWriter.writeHeader(`Delegation of Authority - Study ${studyBinder.title || ''}`);
        const columns = DEFAULT_STUDY_DETAILS_HEADERS;
        const studyDetails = [[
            studyBinder.primaryInvestigator || '', 
            studyBinder.trialId || '', 
            studyBinder.sponsorCro || '', 
            studyBinder.departmentSiteDg || ''
        ]];
        this.pdfWriter.writeTable({head: columns, body: studyDetails, showHead: true});
        this.pdfWriter.writeHorizontalLine();
    }

    /**
     * Uses DOA study tasks to generate study key.
     * @private
     */
    generateStudyKey() {
        this.pdfWriter.writeHeader('Delegation Key');
        const columns = ['left', 'mid', 'right'];
        const formattedStudyKey = lodash.chain(this.DOA.studyTasks)
            .map(studyTask => `${studyTask.taskIndex}. ${studyTask.taskName}`)
            .chunk(columns.length)
            .value();

        this.pdfWriter.writeTable({head: [columns], body: formattedStudyKey, showHead: false, fontSize: 10, fillColor: '#FFF'});
    }

    /**
     * @private
     */
    generateTestamentSection() {
        const testaments = [
            this.getTestament('PI', 'Principal Investigator Testament'),
            this.getTestament('Staff', 'Staff Testament'),
        ];
        testaments.map(testament => {
            this.pdfWriter.writeHeader(testament.title);
            this.pdfWriter.writeTable({body: [[testament.statement]], fontSize: 10, fillColor: '#FFF', extendCursor: 30});
        });

    }

    /**
     * Finds the correct testament for the testament type.
     * @param {String} testamentType 
     * @param {String} title 
     * @private
     */
    getTestament(testamentType, title) {
        const studyTestament = lodash.find(this.DOA.studyTestaments, ['title', testamentType]);
        const testamentText = lodash.get(studyTestament, 'text');
        const statement = testamentText || DEFAULT_TESTAMENT_MAP[testamentType];

        return {
            title,
            statement
        };
    }

    /**
     * @private
     */
    generateDelegationSummary() {
        this.pdfWriter.addPage();
        this.pdfWriter.writeHeader('Delegations', { extendCursor: 20 });
        const formattedDelegations = lodash.map(this.DOA.studyDelegations, 
            delegation => formatDelegationTableRow(delegation, this.DOA));

        return this.pdfWriter.writeTable({head: DEFAULT_DELEGATION_SUMMARY_COLUMN_HEADERS, body: formattedDelegations, showHead:true, fontSize: 10, extendCursor: 30});
    }

    /**
     * @private
     */
    generateSignatureManifestations() {
        this.pdfWriter.addPage();
        this.pdfWriter.writeHeader('Signature Manifestations', { extendCursor: 20 });
        lodash.map(this.DOA.studyDelegations, delegation => this.generateSignatureManifestationSection(delegation));
    }

    /**
     * 
     * @param {Delegation} delegation 
     * @private
     */
    generateSignatureManifestationSection(delegation) {
        const sectionHeader = formatSignatureManifestationHeader(delegation);
        this.pdfWriter.writeHeader(sectionHeader, { extendCursor: 20 });
        const formattedSignatureManifestations = lodash.map(delegation.signatures, 
            delegationSignature => formatDelegationSignatureTableRow(delegationSignature, this.timezoneOffset));

        return this.pdfWriter.writeTable({head: DEFAULT_SIGNATURE_MANIFESTATION_COLUMN_HEADERS, body: formattedSignatureManifestations, showHead:true, fontSize: 10, theme: 'grid'});
    }

    /**
     * Tags each page with the page heading, including page count and export date.
     * @param {String} exportDate 
     * @private
     */
    tagPageHeaders(exportDate) {
        this.pdfWriter.writePageHeaders(exportDate);
    }

    /**
     * @returns {String} Current date.
     * @private
     */
    getExportDate() {
        return moment().format(DATE_FORMAT);
    }

    /**
     * 
     * @param {String} studyTitle 
     * @private
     */
    save() {
        const exportDate = this.getExportDate();
        const studyTitle = this.DOA.studyBinder.title;
        this.tagPageHeaders(exportDate);
        const fileName = `${studyTitle}-delegation-log-${exportDate}.pdf`;
        this.pdfWriter.savePDF(fileName);
    }
}
